import type { BatteryConcertAmountEligibilityStatus } from "~/api/dto/battery-proposal.dto"

import type { ConcertAmountEligibilityView } from "./concert-kind-bundle"

/**
 * How a configured Concert loan-amount range reads to a seller, for any
 * proposal kind. The server owns the verdict; this only puts words and dollars
 * around it, so the range on screen is always the range being enforced.
 */

export const CONCERT_AMOUNT_RANGE_LABEL = `Amount range`
export const CONCERT_AMOUNT_CURRENT_LABEL = `This proposal`
export const CONCERT_AMOUNT_NO_LIMIT = `No limit`
export const CONCERT_AMOUNT_NOT_PRICED = `Not yet priced`

/** Whole dollars, cents only when the amount has them. Never arithmetic. */
const usd = new Intl.NumberFormat(`en-US`, {
  style: `currency`,
  currency: `USD`,
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
})

export const formatConcertAmount = (amount: number | null): string =>
  amount == null ? CONCERT_AMOUNT_NOT_PRICED : usd.format(amount)

/**
 * Whether an administrator has configured a range at all. Until one exists
 * there is nothing for a seller to measure their price against, so the range
 * block stays off the Loan Product step rather than announcing "No limit".
 */
export const hasConfiguredConcertRange = (
  eligibility: ConcertAmountEligibilityView | null,
): boolean =>
  eligibility != null &&
  (eligibility.minimumLoanAmount != null ||
    eligibility.maximumLoanAmount != null)

/** The configured range, however many sides of it exist. */
export const concertAmountRangeText = ({
  minimumLoanAmount,
  maximumLoanAmount,
}: Pick<
  ConcertAmountEligibilityView,
  `maximumLoanAmount` | `minimumLoanAmount`
>): string => {
  if (minimumLoanAmount != null && maximumLoanAmount != null) {
    return `${usd.format(minimumLoanAmount)} – ${usd.format(maximumLoanAmount)}`
  }
  if (minimumLoanAmount != null) {
    return `${usd.format(minimumLoanAmount)} or more`
  }
  if (maximumLoanAmount != null) {
    return `Up to ${usd.format(maximumLoanAmount)}`
  }

  return CONCERT_AMOUNT_NO_LIMIT
}

/**
 * Why Concert is unavailable at this price, naming the bound that failed and
 * the price that failed it. Null whenever the amount is not what is wrong.
 * `financing` names the plan: "Concert Battery financing", "Concert financing".
 */
export const concertAmountBlockedPrompt = (
  eligibility: ConcertAmountEligibilityView | null,
  financing: string,
): string | null => {
  if (eligibility == null) {
    return null
  }
  const blocked: Partial<Record<BatteryConcertAmountEligibilityStatus, string>> =
    {
      below_minimum: `${financing} starts at ${usd.format(
        eligibility.minimumLoanAmount ?? 0,
      )}. This proposal totals ${formatConcertAmount(eligibility.amount)}. Adjust the proposal and refresh pricing, or choose another Finance Partner.`,
      above_maximum: `${financing} tops out at ${usd.format(
        eligibility.maximumLoanAmount ?? 0,
      )}. This proposal totals ${formatConcertAmount(eligibility.amount)}. Adjust the proposal and refresh pricing, or choose another Finance Partner.`,
    }

  return blocked[eligibility.status] ?? null
}
